import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TreeView from "@mui/lab/TreeView";
import TreeItem, { treeItemClasses, useTreeItem } from "@mui/lab/TreeItem";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupsIcon from "@mui/icons-material/Groups";
import { Typography, Box } from "@common-ui/ui";

import TreeOptions from "./TreeOptions";
import { useHistory, useLocation } from "react-router-dom";

const ControlledTreeItem = React.forwardRef(function ControlledTreeItem(
  props,
  ref
) {
  const {
    classes,
    className,
    label,
    nodeId,
    icon: iconProp,
    expansionIcon,
    displayIcon,
  } = props;

  const {
    disabled,
    expanded,
    selected,
    focused,
    handleExpansion,
    handleSelection,
    preventSelection,
  } = useTreeItem(nodeId);

  const icon = iconProp || expansionIcon || displayIcon;

  const handleMouseDown = (event) => {
    preventSelection(event);
  };

  const handleExpansionClick = (event) => {
    handleExpansion(event);
  };

  const handleSelectionClick = (event) => {
    handleSelection(event);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      onMouseDown={handleMouseDown}
      ref={ref}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div onClick={handleExpansionClick} className={classes.iconContainer}>
        {icon}
      </div>
      <Typography
        onClick={handleSelectionClick}
        component="div"
        className={classes.label}
      >
        {label}
      </Typography>
    </div>
  );
});

const CustomTreeItem = (props) => (
  <TreeItem ContentComponent={ControlledTreeItem} {...props} />
);

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const ClientPlacesTreeView = ({
  handleAddSubOrg,
  refresh,
  orgId,
  customerId,
  handleAddSubPlace,
  data,
  clientData,
  setCustomerPlaceId,
}) => {
  const localStoreageExpandedState = window?.localStorage?.getItem(
    "CUSTOMER_PLACE_EXPANDED"
  );
  const [expanded, setExpanded] = useState(
    localStoreageExpandedState ? JSON.parse(localStoreageExpandedState) : []
  );
  const [currentHover, setCurrentHover] = useState(-1);
  let router = useHistory();
  let query = useQuery();

  let selectedPlace = String(query.get("place"));

  useEffect(() => {
    setCustomerPlaceId(selectedPlace);
  }, [setCustomerPlaceId, selectedPlace]);

  const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
    color: theme.palette.text.secondary,
    [`& .${treeItemClasses.content}`]: {
      boxSizing: "border-box",
    },
  }));

  const renderPlace = (place) => {
    return place?.screen_name && String(place?.screen_name).trim() !== ""
      ? place?.screen_name
      : place?.name;
  };

  const renderParentNode = (parents) => {
    return parents.map((parent, i) => {
      return (
        <StyledTreeItem
          key={String(parent.system_id)}
          onMouseEnter={() => setCurrentHover(String(parent.system_id))}
          onMouseLeave={() => setCurrentHover(-1)}
          label={
            <Box sx={{ display: "flex", alignItems: "center", p: 0.5, pr: 0 }}>
              <Box component={LocationOnIcon} color="inherit" sx={{ mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: "inherit", flexGrow: 1 }}
                noWrap
              >
                {renderPlace(parent)}
              </Typography>
              <Typography
                variant="caption"
                color="inherit"
                className="add-icon"
                sx={{
                  opacity:
                    String(parent.system_id) === currentHover ||
                    selectedPlace === String(parent.system_id)
                      ? 1
                      : 0,
                }}
              >
                <TreeOptions
                  handleAddSubPlace={handleAddSubPlace}
                  router={router}
                  nodeId={String(parent.system_id)}
                />
              </Typography>
            </Box>
          }
          nodeId={String(parent.system_id)}
        >
          {renderTree(parent.child_places) || null}
        </StyledTreeItem>
      );
    });
  };

  const renderTree = (nodes) => {
    return Array.isArray(nodes)
      ? nodes.length
        ? nodes.map((node) => (
            <StyledTreeItem
              onMouseEnter={() => setCurrentHover(String(node.system_id))}
              onMouseLeave={() => setCurrentHover(-1)}
              label={
                <Box
                  sx={{ display: "flex", alignItems: "center", p: 0.5, pr: 0 }}
                >
                  <Box
                    component={LocationOnIcon}
                    color="inherit"
                    sx={{ mr: 1 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "inherit", flexGrow: 1 }}
                    noWrap
                  >
                    {renderPlace(node)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="inherit"
                    className="add-icon"
                    sx={{
                      opacity:
                        String(node.system_id) === currentHover ||
                        selectedPlace === String(node.system_id)
                          ? 1
                          : 0,
                    }}
                  >
                    <TreeOptions
                      handleAddSubPlace={handleAddSubPlace}
                      router={router}
                      nodeId={String(node.system_id)}
                    />
                  </Typography>
                </Box>
              }
              key={String(node.system_id)}
              nodeId={String(node.system_id)}
            >
              {renderTree(node.child_places)}
            </StyledTreeItem>
          ))
        : null
      : null;
  };

  return (
    <TreeView
      aria-label="rich object"
      expanded={expanded}
      defaultCollapseIcon={<ArrowDropDownIcon />}
      defaultExpandIcon={<ArrowRightIcon />}
      defaultEndIcon={<div style={{ width: 24 }} />}
      selected={String(selectedPlace)}
      onNodeToggle={(event, nodeIds) => {
        setExpanded(nodeIds);
        window?.localStorage.setItem(
          "CUSTOMER_PLACE_EXPANDED",
          JSON.stringify(nodeIds)
        );
      }}
      sx={{
        maxHeight: "calc(100vh - 55px)",
        flexGrow: 1,
        maxWidth: 360,
        overflow: "auto",
      }}
      onNodeSelect={(_event, nodeIds) => {
        router.push({ search: `?place=${nodeIds}` });
        setCustomerPlaceId(nodeIds);
      }}
    >
      <StyledTreeItem
        label={
          <Box sx={{ display: "flex", alignItems: "center", p: 0.5, pr: 0 }}>
            <Box component={GroupsIcon} color="inherit" sx={{ mr: 1 }} />
            <Typography variant="h5" noWrap>
              {clientData?.name && clientData.name.trim()}
            </Typography>
          </Box>
        }
        nodeId={"overview"}
      />
      {renderParentNode(data)}
    </TreeView>
  );
};
